/**
 * The Bloom — Laseera's mark.
 *
 * A woman's profile in three-quarter turn, her hair opening into a wreath of
 * leaves that closes the circle behind her. Orchid over lilac over one deep
 * plum accent in the crown, which is the only place the darkest value appears.
 *
 * ── Why this is an image and the Doorway was not ──────────────────────────
 *
 * The mark it replaces was two elements — a stroke and a dot — so it was drawn
 * inline: at that complexity a component can take its colour from whatever
 * surface it lands on, and reversing it for a dark row was one ternary. This
 * mark is four flood-filled paths carrying about 88KB of coordinates and three
 * fixed brand colours. Inlining it would put that payload in the render tree at
 * every call site and buy nothing, because a full-colour illustration has no
 * `currentColor` to inherit — so it ships from `public/` as one cacheable file
 * requested once, and the surfaces below are what it was checked against
 * instead of a reversed variant.
 *
 * It was proofed on mist, on white and on ink and holds on all three: the lilac
 * never drops under the ink ground, and the orchid keeps its edge on both light
 * surfaces. The fourth surface it used to be proofed against was the brand
 * fill, and that check is retired rather than re-run. The fill is orchid-700
 * now — the mark's own deep value — and no mark holds against itself. Nothing
 * places it there: the sidebar lockup and the sign-in lockup are the only two
 * call sites and both sit on a light surface. A lockup on a filled brand row
 * would want a reversed artwork, not this file scaled onto it.
 *
 * That is also why `tone` is gone rather than reimplemented — there is no
 * second artwork for it to select, and a prop that silently does nothing is
 * worse than no prop.
 *
 * ── The floor is 24px, and the sidebar sits on it ─────────────────────────
 *
 * The Doorway was drawn to survive to 16px. This one is not: the wreath is
 * about forty separate leaves, and below roughly 24px they merge into a
 * lozenge. It is legible from 24px, comfortable at 32px and clearly itself from
 * 40px. Two consequences, both deliberate:
 *
 *   * The sidebar (26px) and the favicon (16px) are the two places in this
 *     dashboard that ask for less than the mark can give. The sidebar is
 *     one pixel over the floor and reads as a purple wreath rather than as a
 *     face, which is acceptable because the wordmark is beside it.
 *   * The favicon is not. `public/favicon.svg` carries the same artwork and at
 *     a tab's 16px it is a violet smudge. If that matters it wants a drawn
 *     small-size variant — the head silhouette alone, no wreath — rather than
 *     this file scaled down, and that is a design decision, not a code one.
 *
 * Source artwork: `ls-ai/design/logo/colourways/orchid-lilac-hires/
 * laseera-emblem-orchid-lilac.svg`. `public/brand/laseera-emblem.svg` is a copy
 * of it and may not be edited by hand — regenerate both from the source so the
 * dashboard, the two apps and the marketing site cannot drift apart.
 */
export interface BrandMarkProps {
  /** Rendered edge length in pixels. Below 24 the wreath stops resolving. */
  readonly size?: number;
  /**
   * The mark is decorative wherever the wordmark is beside it, which is
   * everywhere it is used today — a screen reader announcing "Laseera Laseera"
   * is worse than one announcing it once.
   */
  readonly title?: string;
}

export function BrandMark({ size = 28, title }: BrandMarkProps) {
  return (
    <img
      src="/brand/laseera-emblem.svg"
      width={size}
      height={size}
      alt={title ?? ''}
      // The artwork is 487×484, a hair wider than tall. Letting it keep that
      // ratio inside a square box leaves the wreath optically centred; forcing
      // it square would squash the profile, which is the one part of the
      // drawing a person actually reads.
      style={{ display: 'block', width: size, height: 'auto' }}
      draggable={false}
    />
  );
}
